import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

// Token işlemleri için yardımcı fonksiyonlar
export const saveTokens = (accessToken, refreshToken) => {
  localStorage.setItem("bigpassToken", accessToken);
  localStorage.setItem("bigpassRefreshToken", refreshToken);
};

export const getTokens = () => {
  const accessToken = localStorage.getItem("bigpassToken");
  const refreshToken = localStorage.getItem("bigpassRefreshToken");
  return { accessToken, refreshToken };
};

export const removeTokens = () => {
  localStorage.removeItem("bigpassToken");
  localStorage.removeItem("bigpassRefreshToken");
};

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const userRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isFetchingRef = useRef(false);
  const refreshFailedRef = useRef(false);

  // Sync user state with ref for stable reads without causing re-renders
  const updateUser = useCallback((newUser) => {
    userRef.current = newUser;
    setUser(newUser);
  }, []);

  // Helper function to redirect to login
  const redirectToLogin = useCallback(() => {
    if (
      window.location.pathname !== "/login" &&
      window.location.hash !== "#/login" &&
      !window.location.href.includes("/login")
    ) {
      removeTokens();
      window.location.href = "/#/login";
    }
  }, []);

  // Token yenileme işlemi
  const refreshTokens = useCallback(async () => {
    if (isRefreshingRef.current) {
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!isRefreshingRef.current) {
            clearInterval(checkInterval);
            if (refreshFailedRef.current) {
              reject(new Error("Refresh token expired"));
            } else {
              const { accessToken } = getTokens();
              resolve(accessToken);
            }
          }
        }, 100);
      });
    }

    if (refreshFailedRef.current) {
      throw new Error("Refresh token expired");
    }

    const { refreshToken } = getTokens();

    if (!refreshToken) {
      refreshFailedRef.current = true;
      removeTokens();
      updateUser(null);
      redirectToLogin();
      throw new Error("No refresh token available");
    }

    isRefreshingRef.current = true;

    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refreshToken: refreshToken,
      });

      if (response.data && response.data.success) {
        saveTokens(response.data.token, response.data.refreshToken);
        refreshFailedRef.current = false;
        return response.data.token;
      } else {
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      refreshFailedRef.current = true;
      removeTokens();
      updateUser(null);
      redirectToLogin();
      throw error;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [redirectToLogin, updateUser]);

  // Tekil Axios interceptor'ı ekle - YALNIZCA BİR KEZ ÇALIŞIR
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (refreshFailedRef.current) {
          return Promise.reject(error);
        }

        const isRefreshEndpoint =
          originalRequest?.url?.includes("/api/auth/refresh");
        if (isRefreshEndpoint && error.response?.status === 401) {
          refreshFailedRef.current = true;
          removeTokens();
          updateUser(null);
          redirectToLogin();
          return Promise.reject(error);
        }

        if (
          error.response?.status === 401 &&
          !originalRequest?._retry &&
          !isRefreshEndpoint
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await refreshTokens();
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            }
            return axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [redirectToLogin, refreshTokens, updateUser]);

  // Token ile kullanıcı bilgisini çek - ASLA DÖNGÜYE GİRMEZ
  const getUserFromToken = useCallback(
    async (force = false) => {
      if (isFetchingRef.current) {
        return userRef.current;
      }

      const { accessToken } = getTokens();
      if (!accessToken) {
        updateUser(null);
        return null;
      }

      isFetchingRef.current = true;

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.data && response.data.success && response.data.user) {
          updateUser(response.data.user);
          return response.data.user;
        } else {
          removeTokens();
          updateUser(null);
          return null;
        }
      } catch (apiError) {
        if (refreshFailedRef.current) {
          removeTokens();
          updateUser(null);
          return null;
        }

        if (apiError.response?.status === 401) {
          try {
            const newToken = await refreshTokens();
            if (newToken) {
              const retryResponse = await axios.get(`${API_URL}/api/auth/me`, {
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              });
              if (retryResponse.data?.success && retryResponse.data?.user) {
                updateUser(retryResponse.data.user);
                return retryResponse.data.user;
              }
            }
          } catch (refreshError) {
            removeTokens();
            updateUser(null);
            return null;
          }
        }

        // 401 dışındaki veya bağlantı hatalarında hemen token silme, user'ı koru/boşalt
        updateUser(null);
        return null;
      } finally {
        isFetchingRef.current = false;
      }
    },
    [refreshTokens, updateUser],
  );

  // Uygulama açılışında tek seferlik kontrol - BAĞIMLILIK BOŞTUR
  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        await getUserFromToken(true);
      } catch (error) {
        removeTokens();
        updateUser(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, []); // <-- STRICTLY ONCE ON MOUNT

  const login = useCallback(
    async (email, password) => {
      try {
        const response = await axios.post(`${API_URL}/api/auth/login`, {
          email,
          password,
        });

        if (response.data && response.data.token) {
          refreshFailedRef.current = false;
          isRefreshingRef.current = false;

          saveTokens(response.data.token, response.data.refreshToken);

          if (response.data.user) {
            updateUser(response.data.user);
          }

          return { success: true };
        } else {
          return { success: false, message: "Giriş başarısız: Token alınamadı" };
        }
      } catch (error) {
        const backendMessage =
          error?.response?.data?.message || error?.response?.data?.error;
        return {
          success: false,
          message:
            backendMessage ||
            error.message ||
            "Giriş işlemi sırasında bir hata oluştu",
        };
      }
    },
    [updateUser],
  );

  const logout = useCallback(() => {
    try {
      refreshFailedRef.current = false;
      isRefreshingRef.current = false;

      axios.post(`${API_URL}/api/auth/logout`, {}).catch(() => {});

      removeTokens();
      updateUser(null);
      return true;
    } catch (err) {
      return false;
    }
  }, [updateUser]);

  const getToken = useCallback(() => {
    const { accessToken } = getTokens();
    return accessToken;
  }, []);

  const getUser = useCallback(() => {
    return userRef.current;
  }, []);

  const value = {
    user,
    setUser: updateUser,
    loading,
    getUser,
    getToken,
    getUserFromToken,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    role: user?.role || "dealer",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    const { accessToken } = getTokens();
    return {
      user: null,
      loading: false,
      getUser: () => null,
      getToken: () => accessToken,
      getUserFromToken: async () => null,
      login: async () => ({ success: false }),
      logout: () => {},
      isAuthenticated: false,
      isAdmin: false,
      role: "dealer",
    };
  }
  return context;
};

export default useAuth;
